import { useContext } from 'react';
import {
  convertFiltersToPayloadKeys,
  getFilterSetTrackingPayload,
  mapContextToTrackingPage,
  supportedFiltersTracking
} from './tracking-events.utils';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import useTrackingPage from './useTrackingPage';

const useEventsTracker = ({ Context }) => {
  const {
    state: { localFilters: filters }
  } = useContext(Context);
  const page = useTrackingPage();

  const trackEvent = (eventName, payload) => {
    if (!supportedFiltersTracking.includes(Context.name)) return;

    TrackingService.logEvent(eventName, {
      page,
      ...payload
    });
  };

  const onApplyFilters = () => {
    const filtersKeys = convertFiltersToPayloadKeys(filters);
    trackEvent(TRACKING.EVENTS.FILTER, {
      filter_name: TRACKING.CLICK.FILTERS_BAR.APPLY_FILTER,
      filter_value: filtersKeys
    });
  };

  const applyFiltersCTA = {
    proceedToSave: dontShowAgain =>
      trackEvent(TRACKING.EVENTS.CLICK, {
        click_type:
          TRACKING.CLICK.FILTERS_BAR.FILTER_SET.APPLY_FILTERS_CTA
            .PROCEED_TO_SAVE,
        unsubscribed: dontShowAgain
      }),
    applyWithoutSaving: dontShowAgain =>
      trackEvent(TRACKING.EVENTS.CLICK, {
        click_type:
          TRACKING.CLICK.FILTERS_BAR.FILTER_SET.APPLY_FILTERS_CTA
            .APPLY_WITHOUT_SAVING,
        unsubscribed: dontShowAgain
      })
  };

  const onOpenFilterSetMenu = () =>
    trackEvent(TRACKING.EVENTS.HOVER, {
      click_type: TRACKING.CLICK.FILTERS_BAR.FILTER_SET.MENU.OPEN
    });

  const onSelectFilterSetMenuOption = {
    save: () =>
      trackEvent(TRACKING.EVENTS.CLICK, {
        click_type: TRACKING.CLICK.FILTERS_BAR.FILTER_SET.MENU.CLICK.SAVE
      }),
    manage: () =>
      trackEvent(TRACKING.EVENTS.CLICK, {
        click_type: TRACKING.CLICK.FILTERS_BAR.FILTER_SET.MENU.CLICK.MANAGE
      }),
    clear: () =>
      trackEvent(TRACKING.EVENTS.CLICK, {
        click_type: TRACKING.CLICK.FILTERS_BAR.FILTER_SET.MENU.CLICK.CLEAR
      })
  };

  const onDeleteFilterSet = filterSet =>
    trackEvent(TRACKING.EVENTS.CLICK, {
      click_type: TRACKING.CLICK.FILTERS_BAR.FILTER_SET.CRUD.DELETE,
      ...getFilterSetTrackingPayload(filterSet)
    });

  const onUpsertFilterSet = filterSet =>
    trackEvent(TRACKING.EVENTS.CLICK, {
      click_type: Boolean(filterSet?.id)
        ? TRACKING.CLICK.FILTERS_BAR.FILTER_SET.CRUD.EDIT
        : TRACKING.CLICK.FILTERS_BAR.FILTER_SET.CRUD.CREATE,
      ...getFilterSetTrackingPayload(filterSet)
    });

  const onSelectFilterSet = filterSet =>
    trackEvent(TRACKING.EVENTS.CLICK, {
      click_type: TRACKING.CLICK.FILTERS_BAR.FILTER_SET.SELECT,
      ...getFilterSetTrackingPayload(filterSet)
    });

  const onFilterSetNavigation = (filterSet, destinationContext) => {
    const destinationPage = mapContextToTrackingPage[destinationContext?.name];
    trackEvent(TRACKING.EVENTS.CLICK, {
      click_type: TRACKING.CLICK.FILTERS_BAR.FILTER_SET.NAVIGATION,
      destination_page: destinationPage,
      ...getFilterSetTrackingPayload(filterSet)
    });
  };

  return {
    onApplyFilters,
    onOpenFilterSetMenu,
    onSelectFilterSetMenuOption,
    onDeleteFilterSet,
    onUpsertFilterSet,
    onSelectFilterSet,
    onFilterSetNavigation,
    applyFiltersCTA
  };
};

export default useEventsTracker;
